/**
 * 媒体发送入口单点（12b/14.1b/14.3b）：Composer 按钮/粘贴与 ChatPage 拖拽共用。
 * - sendFiles：纯图片路径（粘贴/图片按钮）。逐张顺序受理（每张一条消息）；
 *   多选 ≥2 张按 planAlbumExts 切相册段（每段独立 album_id ext 透传发送链，
 *   渲染端按相邻性自动合并网格）。
 * - sendAttachments：附件路径（附件按钮/拖拽，任意类型）。按 fileSend.ts 分流：
 *   图片归并进 sendFiles（享相册合并），视频 msgType 7 / 其余文件 msgType 8
 *   原件直传（旧版「按扩展名判定」同口径）。
 * 拒绝/失败以瞬时提示条展示（3s 自清），文案映射在 composerModel。
 */
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { planAlbumExts } from '~/chat/albumModel';
import { attachmentKindFor } from '~/chat/fileSend';
import { messagesConnection } from '~/store/appWorker';
import { uuidV4 } from '~/util/uuid';
import { fileSendErrorText, imageSendErrorText } from './composerModel';

const TIP_MS = 3_000;

export interface ImageSender {
  /** 当前提示文案（空串=无提示） */
  readonly tip: string;
  /** 纯图片（粘贴/图片按钮已按 MIME 过滤） */
  sendFiles(files: readonly File[]): void;
  /** 任意附件（附件按钮/拖拽）：图片/视频/文件自动分流 */
  sendAttachments(files: readonly File[]): void;
}

export function useImageSender(): ImageSender {
  const [tip, setTip] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(
    () => () => {
      if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    },
    [],
  );

  const showTip = useCallback((text: string) => {
    if (text === '') return;
    setTip(text);
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setTip(''), TIP_MS);
  }, []);

  const sendFiles = useCallback(
    (files: readonly File[]) => {
      void (async () => {
        const exts = planAlbumExts(files.length, uuidV4);
        for (const [i, file] of files.entries()) {
          const outcome = await messagesConnection().sendImage(file, exts[i]);
          showTip(imageSendErrorText(outcome));
        }
      })();
    },
    [showTip],
  );

  const sendAttachments = useCallback(
    (files: readonly File[]) => {
      const images: File[] = [];
      const rest: { file: File; fileKind: 'file' | 'video' }[] = [];
      for (const file of files) {
        const kind = attachmentKindFor(file.name, file.type);
        if (kind === 'image') images.push(file);
        else rest.push({ file, fileKind: kind });
      }
      if (images.length > 0) sendFiles(images);
      void (async () => {
        for (const { file, fileKind } of rest) {
          const outcome = await messagesConnection().sendFile(file, fileKind);
          showTip(fileSendErrorText(outcome, fileKind === 'video'));
        }
      })();
    },
    [sendFiles, showTip],
  );

  return { tip, sendFiles, sendAttachments };
}
